<template>
  <div class="ic-modal-body">
    <h2 class="ic-modal-body__title">Edit Full Name</h2>
    <form
      class="ic-modal-body__form"
      @submit="handleSubmit"
      method="POST"
      autocomplete="on"
    >
      <div class="ic-modal-body__form-fields">
        <div class="ic-modal-body__form-field">
          <label
            for="firstName"
            class="ic-modal-body__form-label"
            v-text="'First Name'"
          />
          <input
            type="text"
            id="firstName"
            name="first_name"
            class="ic-modal-body__form-input"
            :class="{
              'ic-modal-body__form-input--error':
                error.firstName || errors.firstName
            }"
            v-model="form.firstName"
            @input="updateName('firstName')"
            @keypress="handleLettersAndDashes"
            @blur="handleBlur"
            required
          />
          <p class="ic-modal-body__error-message" v-if="error.firstName">
            {{ errorMessage }}
          </p>
          <p class="ic-modal-body__error-message" v-if="errors.firstName">
            {{ errors.firstName }}
          </p>
        </div>
        <div class="ic-modal-body__form-field">
          <label
            for="middleName"
            class="ic-modal-body__form-label"
            v-text="'Middle Name'"
          />
          <input
            type="text"
            id="middleName"
            name="middle_name"
            class="ic-modal-body__form-input"
            :class="{
              'ic-modal-body__form-input--error': errors.middleName
            }"
            v-model="form.middleName"
            @input="updateName('middleName')"
            @keypress="handleLettersAndDashes"
          />
          <p class="ic-modal-body__error-message" v-if="errors.middleName">
            {{ errors.middleName }}
          </p>
        </div>
        <div class="ic-modal-body__form-field">
          <label
            for="lastName"
            class="ic-modal-body__form-label"
            v-text="'Last Name'"
          />
          <input
            type="text"
            id="lastName"
            name="last_name"
            class="ic-modal-body__form-input"
            :class="{
              'ic-modal-body__form-input--error':
                error.lastName || errors.lastName
            }"
            v-model="form.lastName"
            @input="updateName('lastName')"
            @keypress="handleLettersAndDashes"
            @blur="handleBlur"
            required
          />
          <p class="ic-modal-body__error-message" v-if="error.lastName">
            {{ errorMessage }}
          </p>
          <p class="ic-modal-body__error-message" v-if="errors.lastName">
            {{ errors.lastName }}
          </p>
        </div>
      </div>
      <div class="ic-modal-body__form-buttons">
        <button
          type="submit"
          class="ic-modal-body__form-button"
          :class="{ 'ic-modal-body__form-button--loading': isSubmitting }"
          :disabled="hasInputsChanged || isSubmitting"
          v-text="submitButtonText"
        />
        <button
          class="ic-modal-body__form-button ic-modal-body__form-button--cancel"
          v-touch:tap="closeModal"
          :disabled="isSubmitting"
          v-text="cancelButtonText"
        />
      </div>
    </form>
  </div>
</template>

<script>
import helpers from '@/assets/js/shared/misc/helpers';

export default {
  name: 'ic-edit-name',
  props: {
    // Value used for the first name field
    firstName: {
      type: String,
      required: true
    },
    // Value used for the middle name field
    middleName: {
      type: String
    },
    // Value used for the last name field
    lastName: {
      type: String,
      required: true
    },
    // Text to display for the submit button
    submitButtonText: {
      type: String,
      required: true
    },
    // Text to display for the cancel button
    cancelButtonText: {
      type: String,
      required: true
    },
    // Checks if form is being submitted
    isSubmitting: {
      type: Boolean,
      required: true
    },
    // Contains errors for each field
    errors: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      hasFirstNameChanged: null,
      hasMiddleNameChanged: null,
      hasLastNameChanged: null,
      form: {
        firstName: this.firstName,
        middleName: this.middleName,
        lastName: this.lastName
      },
      error: {
        firstName: false,
        lastName: false
      },
      errorMessage: 'This field is required'
    };
  },
  computed: {
    hasInputsChanged() {
      return (
        !this.hasFirstNameChanged &&
        !this.hasMiddleNameChanged &&
        !this.hasLastNameChanged
      );
    }
  },
  methods: {
    closeModal() {
      this.$emit('ic-edit-name-close-modal');
    },
    /**
     * Updates user's first, middle and last name
     *
     * @param {string} type
     */
    updateName(type) {
      const firstNameVal = this.firstName ?? '';
      const formFirstNameVal = this.form?.firstName ?? '';
      const middleNameVal = this.middleName ?? '';
      const formMiddleNameVal = this.form?.middleName ?? '';
      const lastNameVal = this.lastName ?? '';
      const formLastNameVal = this.form?.lastName ?? '';

      switch (type) {
        case 'firstName':
          this.hasFirstNameChanged =
            firstNameVal.toLowerCase() !== formFirstNameVal.toLowerCase();
          break;
        case 'middleName':
          this.hasMiddleNameChanged =
            middleNameVal.toLowerCase() !== formMiddleNameVal.toLowerCase();
          break;
        case 'lastName':
          this.hasLastNameChanged =
            lastNameVal.toLowerCase() !== formLastNameVal.toLowerCase();
      }
    },
    /**
     * Allow letters and dashes on the field
     * @param {object} event
     */
    handleLettersAndDashes(event) {
      let value = null;
      let isEnterKey;

      if (event.type === 'keypress') {
        const charCode = event.charCode ? event.charCode : event.which;
        value = String.fromCharCode(charCode);
        isEnterKey = charCode === 13;
        this.isUpdateDisabled = false;
      }

      if (!helpers.lettersAndDashes.test(value) && !isEnterKey) {
        event.preventDefault();
      }
      return false;
    },
    /**
     *
     * @param event
     */
    handleBlur(event) {
      const fieldName = event.target.name;

      if (fieldName === 'first_name' && this.form.firstName === '') {
        this.error.firstName = true;
      } else {
        this.error.firstName = false;
      }
      if (fieldName === 'last_name' && this.form.lastName === '') {
        this.error.lastName = true;
      } else {
        this.error.lastName = false;
      }
    },
    /**
     * Emit an event only if an input field has changed
     *
     * @param event
     */
    handleSubmit(event) {
      event.preventDefault();

      if (this.hasFirstNameChanged) {
        this.$emit('update-first-name', this.form.firstName);
      }
      if (this.hasMiddleNameChanged) {
        this.$emit('update-middle-name', this.form.middleName);
      }
      if (this.hasLastNameChanged) {
        this.$emit('update-last-name', this.form.lastName);
      }
    }
  }
};
</script>
